import React, { useMemo, useEffect, useState } from 'react';

// Lib
import { useEvent } from 'lib/hooks';
import { shallowEqual } from 'react-redux';
import { getDriverInstance } from 'lib/driver';
import { createTabsConfig } from 'pages/Search/utils';
import { useRegisterTutorialKey } from 'lib/hooks/useTutorial';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Navigation
import { useNavigate } from 'react-router-dom';

// Components
import List from './components/List';
import Button from 'components/UiKit/Button';
import ButtonList from 'components/ButtonList';
import ScrollButton from 'components/ScrollButton';
import PricesOnAsking from 'components/PricesOnAsking';
import DebounceInput from 'components/UiKit/DebounceInput';
import FormatOptionLabel from 'components/FormatOptionLabel';
import CustomMessage from 'components/Messages/CustomMessage';
import PaginationView from 'components/PaginationView/PaginationView';

// Hooks
import { useSearchHandlers } from 'pages/Search/useHandlers/useSearchTextHandler';

// Constants
import { getValueSize } from 'lib/helpers';
import { sizeOptions20 } from 'constans/options';
import { typeTextSearch } from 'pages/Search/utils';
import * as actionsCreator from 'pages/Search/reducer';

// Type
import { TNewData } from 'pages/Search/type';

export const TextSearch = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const registerTutorialKey = useRegisterTutorialKey();

	const {
		value,
		message,
		loading,
		typeMessage,
		currentValue,
		isHiddenDropdown,
		isShowTextSearch,

		searchTextResults,
		searchTextSuggestResults,

		recentSearchList,

		loadingDrugsTab,
		loadingCompoundsTab,
		loadingLibrariesTab,
		loadingImpuritiesTab,
		loadingChemicalProbesTab,
	} = useAppSelector(
		(store) => {
			return {
				value: store.search.searchTextView.value || '',
				message: store.search.searchTextView.message,
				loading: store.search.searchTextView.loading,
				typeMessage: store.search.searchTextView.typeMessage,
				currentValue: store.search.searchTextView.currentValue,
				isHiddenDropdown: store.search.searchTextView.isHiddenDropdown,
				isShowTextSearch: store.search.searchTextView.isShowTextSearch,

				searchTextResults: store.search.searchTextResults,
				searchTextSuggestResults: store.search.searchTextSuggestResults,

				recentSearchList: store.search.recentSearchList,

				loadingDrugsTab: store.search.loadingDrugsTab,
				loadingCompoundsTab: store.search.loadingCompoundsTab,
				loadingLibrariesTab: store.search.loadingLibrariesTab,
				loadingImpuritiesTab: store.search.loadingImpuritiesTab,
				loadingChemicalProbesTab: store.search.loadingChemicalProbesTab,
			};
		},
		shallowEqual
	);

	const drv = getDriverInstance();

	const {
		size,
		goTo,
		number,
		suggest,
		typeData,
		totalPages,
	} = useMemo(() => {
		const t = searchTextResults?.type || '';
		return {
			typeData: t,
			size: searchTextResults && t ? (searchTextResults as Record<any, any>)[t]?.size : 0,
			number: searchTextResults && t ? (searchTextResults as Record<string, any>)[t]?.number : 0,
			totalPages: searchTextResults && t ? (searchTextResults as Record<string, any>)[t]?.totalPages : 0,
			suggest: searchTextSuggestResults && t ? (searchTextSuggestResults as Record<string, any>)[t] : [],
			goTo: searchTextResults && t ? (searchTextResults as Record<string, any>).searchDataKeyBoardGoTo[t]?.goTo : 1
		};
	}, [searchTextResults, searchTextSuggestResults]);

	const [isShowLoader, setIsShowLoader] = useState(false);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [navigate]);

	const onChangeInput = useEvent((value: string): void => {
		dispatch(actionsCreator.searchText({ value: value, isHistory: false, type: '' }));

		if (!drv?.isActive()) {
			dispatch(actionsCreator.handleHiddenDropdown(false));
		}

		setIsShowLoader(false);
	});

	const handlers = useSearchHandlers({
		drv,
		goTo,
		value,
		number,
		typeData,
		totalPages,
		currentValue,
		setIsShowLoader,
	});

	useEffect(() => {
		const content = searchTextResults[typeData as keyof TNewData]?.content;
		registerTutorialKey("search-screen", () => true, "normal", "general");
		registerTutorialKey("search-screen-list", () => !!content?.length, "normal", "general");
	}, [
		loading,
		typeData,
		searchTextResults,
		registerTutorialKey,
	]);

	const {
		onPagePress,
		onNextPressLast,
		onPrevPressLast,

		handleKeyDown,
		handlerSubmit,
		leftIconAction,
		handleClickType,
		handleClickRecentItem,

		onKeyPress,
		handlerSubmitGoTo,
		handlerChangeGoTo,
		handlerChangeSize,

		rightIconAction
	} = handlers;

	const valueSize = getValueSize({
		opt: size,
		options: sizeOptions20,
	});

	const configTabs = createTabsConfig({
		types: typeTextSearch,
		data: searchTextResults,
		isShow: isShowTextSearch,

		loadingByType: isShowLoader ? {
			drugs: loadingDrugsTab,
			compounds: loadingCompoundsTab,
			libraries: loadingLibrariesTab,
			impurities: loadingImpuritiesTab,
			chemicalProbes: loadingChemicalProbesTab,
		} : {},
	});

	return (
		<>
			<ScrollButton />

			<div
				className="search-screen__wrapper-bar flex flex-ai-start flex-jc-center flex-ai-center-mobile flex-direction-column-mobile">
				<DebounceInput
					value={value || ''}

					isHiddenDropdown={isHiddenDropdown || !!drv?.isActive()}

					isLeftIcon
					isRightIcon
					leftIconAction={leftIconAction}
					rightIconAction={rightIconAction}

					minLength={1}
					message={message}
					suggest={suggest}
					loading={loading}
					debounceTimeout={300}
					uniqId="debounce-input"
					onChange={onChangeInput}
					typeMessage={typeMessage}
					onKeyDown={handleKeyDown}
					Children={FormatOptionLabel}
					recentList={recentSearchList || []}
					placeholder="Name, EBC ID, CAS, etc."
					clickRecentItem={handleClickRecentItem}
				/>

				<Button
					type="button"
					label="Search"
					variable="info"
					id="button-search"
					typePadding="middle"
					onPress={handlerSubmit}
					className="ml30 search-screen__search-btn"
				/>
			</div>

			<ButtonList
				type={typeData}
				uniqId="list-tabs"
				config={configTabs}
				onClickType={handleClickType}
				className="search-screen__btn-tab flex flex-ai-center mt10 flex-direction-column-mobile"
			/>

			<div className="full-width search-screen__tabs">
				{((totalPages || 0) > 1 && isShowTextSearch) &&
					<PaginationView
						isWhite
						isShowSelect

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={handlerSubmitGoTo}
						handlerChangeGoTo={handlerChangeGoTo}

						number={number}
						totalPages={totalPages}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={sizeOptions20}
						handlerChangeSize={handlerChangeSize}
					/>}
			</div>

			{isShowTextSearch && <List
				type={typeData}
				currentValue={currentValue}
				data={searchTextResults ? searchTextResults[typeData as keyof TNewData]?.content : []}
			/>}

			{((totalPages || 0) > 1 && isShowTextSearch) &&
				<div className="flex flex-ai-center flex-jc-between pt15 full-width flex-direction-column-mobile">
					<PricesOnAsking />
					<PaginationView
						isWhite

						goTo={goTo}
						onKeyPress={onKeyPress}
						handlerSubmitGoTo={handlerSubmitGoTo}
						handlerChangeGoTo={handlerChangeGoTo}

						number={number}
						totalPages={totalPages}
						onPagePress={onPagePress}
						onNextPressLast={onNextPressLast}
						onPrevPressLast={onPrevPressLast}

						valueSize={valueSize}
						optionsSize={sizeOptions20}
						handlerChangeSize={handlerChangeSize}
					/>
				</div>}

			{searchTextResults && searchTextResults[typeData as keyof TNewData]?.content?.length && isShowTextSearch
				? <CustomMessage
					title="Notice"
					className="mt30"
					text="Due to the dynamic change of the Restricted Product list, if the above material is included in the Restricted Product list or regulated as controlled material by the government, we will not sell it."
				/>
				: null}
		</>
	);
};

export default TextSearch;